import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Users, Plane, Clock, Bell, DollarSign, UserCog, Ticket, CalendarOff, History } from 'lucide-react';

// Admin Components
import AdminStats from '../components/admin/AdminStats';
import BookingsCRMTab from '../components/admin/BookingsCRMTab';
import AdminTripsTab from '../components/admin/AdminTripsTab';
import AdminUsersTab from '../components/admin/AdminUsersTab';
import AdminWaitlistTab from '../components/admin/AdminWaitlistTab';
import PaymentRemindersTab from '../components/admin/PaymentRemindersTab';
import AdminPromoCodesTab from '../components/admin/AdminPromoCodesTab';
import AdminBlackoutDatesTab from '../components/admin/AdminBlackoutDatesTab';
import ActivityLogsTab from '../components/admin/ActivityLogsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token, isStaff, hasPermission, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isStaff) {
      toast.error('Staff access required');
      navigate('/member');
      return;
    }
    fetchData();
  }, [user, token, isStaff, authLoading]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch trips (all staff can see)
      const tripsRes = await axios.get(`${API}/admin/trips`, { headers });
      setTrips(tripsRes.data);
      
      // Fetch stats only if user has permission
      if (hasPermission('can_view_stats')) {
        try {
          const statsRes = await axios.get(`${API}/admin/stats`, { headers });
          setStats(statsRes.data);
        } catch (e) {
          console.log('No stats permission');
        }
      }
      
      // Fetch bookings only if user has permission
      if (hasPermission('can_manage_bookings')) {
        try {
          const bookingsRes = await axios.get(`${API}/admin/bookings`, { headers });
          setBookings(bookingsRes.data);
        } catch (e) {
          console.log('No bookings permission');
        }
      }
      
      // Fetch users only if user has permission
      if (hasPermission('can_manage_users')) {
        try {
          const usersRes = await axios.get(`${API}/admin/users`, { headers });
          setUsers(usersRes.data);
        } catch (e) {
          console.log('No users permission');
        }
      }
      
      // Fetch waitlist only if user has permission
      if (hasPermission('can_manage_waitlist')) {
        try {
          const waitlistRes = await axios.get(`${API}/admin/waitlist`, { headers });
          setWaitlist(waitlistRes.data);
        } catch (e) {
          console.log('No waitlist permission');
        }
      }
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" />
      </div>
    );
  }

  // Determine the default tab based on permissions
  const getDefaultTab = () => {
    if (hasPermission('can_manage_bookings')) return 'bookings';
    if (hasPermission('can_manage_trips')) return 'trips';
    return 'trips';
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-primary">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              {user?.role === 'admin' && 'Full access - Manage trips, bookings, and users'}
              {user?.role === 'moderator' && 'Moderator access - Manage trips and bookings'}
              {user?.role === 'editor' && 'Editor access - Manage trip content'}
            </p>
          </div>

          {/* Stats - only show if user has permission */}
          {hasPermission('can_view_stats') && <AdminStats stats={stats} />}

          {/* Tabs */}
          <Tabs defaultValue={getDefaultTab()} className="space-y-6">
            <TabsList className="grid w-full grid-cols-8 lg:w-auto lg:inline-flex">
              {hasPermission('can_manage_bookings') && (
                <TabsTrigger value="bookings" className="flex items-center gap-2" data-testid="admin-tab-bookings">
                  <DollarSign className="h-4 w-4" />
                  <span className="hidden sm:inline">Customers</span>
                </TabsTrigger>
              )}
              {hasPermission('can_manage_trips') && (
                <TabsTrigger value="trips" className="flex items-center gap-2" data-testid="admin-tab-trips">
                  <Plane className="h-4 w-4" />
                  <span className="hidden sm:inline">Trips</span>
                </TabsTrigger>
              )}
              {hasPermission('can_manage_blackout_dates') && (
                <TabsTrigger value="blackout" className="flex items-center gap-2" data-testid="admin-tab-blackout">
                  <CalendarOff className="h-4 w-4" />
                  <span className="hidden sm:inline">Blackout</span>
                </TabsTrigger>
              )}
              {hasPermission('can_manage_promo_codes') && (
                <TabsTrigger value="promo" className="flex items-center gap-2" data-testid="admin-tab-promo">
                  <Ticket className="h-4 w-4" />
                  <span className="hidden sm:inline">Promos</span>
                </TabsTrigger>
              )}
              {hasPermission('can_manage_users') && (
                <TabsTrigger value="users" className="flex items-center gap-2" data-testid="admin-tab-users">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Users</span>
                </TabsTrigger>
              )}
              {hasPermission('can_manage_waitlist') && (
                <TabsTrigger value="waitlist" className="flex items-center gap-2" data-testid="admin-tab-waitlist">
                  <Clock className="h-4 w-4" />
                  <span className="hidden sm:inline">Waitlist</span>
                </TabsTrigger>
              )}
              {hasPermission('can_send_reminders') && (
                <TabsTrigger value="reminders" className="flex items-center gap-2" data-testid="admin-tab-reminders">
                  <Bell className="h-4 w-4" />
                  <span className="hidden sm:inline">Reminders</span>
                </TabsTrigger>
              )}
              {hasPermission('can_manage_users') && (
                <TabsTrigger value="activity" className="flex items-center gap-2" data-testid="admin-tab-activity">
                  <History className="h-4 w-4" />
                  <span className="hidden sm:inline">Activity</span>
                </TabsTrigger>
              )}
            </TabsList>

            {hasPermission('can_manage_bookings') && (
              <TabsContent value="bookings">
                <BookingsCRMTab 
                  bookings={bookings} 
                  token={token} 
                  onRefresh={fetchData} 
                />
              </TabsContent>
            )}

            {hasPermission('can_manage_trips') && (
              <TabsContent value="trips">
                <AdminTripsTab 
                  trips={trips} 
                  token={token} 
                  onRefresh={fetchData} 
                />
              </TabsContent>
            )}

            {hasPermission('can_manage_blackout_dates') && (
              <TabsContent value="blackout">
                <AdminBlackoutDatesTab trips={trips} />
              </TabsContent>
            )}

            {hasPermission('can_manage_promo_codes') && (
              <TabsContent value="promo">
                <AdminPromoCodesTab trips={trips} />
              </TabsContent>
            )}

            {hasPermission('can_manage_users') && (
              <TabsContent value="users">
                <AdminUsersTab 
                  users={users} 
                  token={token} 
                  onRefresh={fetchData}
                  currentUserEmail={user?.email}
                />
              </TabsContent>
            )}

            {hasPermission('can_manage_waitlist') && (
              <TabsContent value="waitlist">
                <AdminWaitlistTab 
                  waitlist={waitlist} 
                  trips={trips} 
                  token={token} 
                  onRefresh={fetchData} 
                />
              </TabsContent>
            )}

            {hasPermission('can_send_reminders') && (
              <TabsContent value="reminders">
                <PaymentRemindersTab />
              </TabsContent>
            )}

            {hasPermission('can_manage_users') && (
              <TabsContent value="activity">
                <ActivityLogsTab token={token} />
              </TabsContent>
            )}
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
